import React from "react";
import { Button } from "reactstrap";
import AdvancedFilterMenu from "../AdvancedFilterMenu/AdvancedFilterMenu";
import QuickFilterMenu from "../QuickFilterMenu/QuickFilterMenu";
import { Whisper, Popover, Nav } from "rsuite";
import DropdownOptionWithIcon from "../Menus/DropdownOptionWithIcon";
import { callFunctionIfExists } from "../../utils/GeneralUtils";
import FiltersOptions from "../SavedFilters/FiltersOptions";
import { filterIsSame, isValidQuery } from "../../utils/QueryUtils";

/**
 * startngMode - What mode the FilterDropdownMenu should start in "quick" or "advanced"
 * query
 * onModeChange - When the mode is changed to "quick" or "advanced"
 * onApply - Callback for when filter is applied
 * checkForFields - Whether or not to check for fields when validating query.
 * onSaveAsDefault - Callback for when query is saved as default.
 * onClear - Callback for when query is cleared.
 * subItem - Whether or not the filter is for a subitem.
 * customClearButton - Functional component to replace default clear button
 * saveAs - Whether or not to show save button
 * customSaveButton - Functional component to replace save button
 * defaultQuery - The saved query for whatever feature this Menu is being used for.
 * currentModule - The current module we are creating filters for
 * subitemFieldsOnly - Whether or not to show subitem fields only in the fields dropdown
 * fields - Expects the fields that can be used for querying.
 * applyOnChange - Whether or not to call onApply callback whenever the filter changes.
 * showVariables - Whether or not you can toggle variables in Advanced mode
 * onClose - Callback for when FilterDropdownMenu is closed.
 */

class FilterDropdownMenu extends React.Component {
  state = {
    allowAdvancedApply: false,
    advancedFilterQuery: null,
    filterMode: "quick",
    quickFilterFields: [],
  };

  componentDidMount() {
    if (this.props.startingMode) {
      this.setState({ filterMode: this.props.startingMode });
    }

    if (this.props.query) {
      this.setState({
        advancedFilterQuery: this.props.query,
      });
    }

    callFunctionIfExists(
      this.props.onModeChange,
      this.props.startingMode || this.state.filterMode
    );
  }

  componentDidUpdate(prevProps) {
    if (prevProps.query !== this.props.query) {
      this.setState({ advancedFilterQuery: this.props.query });
    }
  }

  /**
   * Handles applying the query.
   */
  applyAdvancedFilter(query, closeMenu = true, type) {
    this.setState({ advancedFilterQuery: query });

    if (typeof this.props?.onApply === "function") {
      this.props.onApply(query, closeMenu, type);
    }
  }

  /**
   * Handles actions to save the current query as the default.
   *
   * @param {*} query
   */
  saveAsDefault(query) {
    let newQuery = JSON.parse(JSON.stringify(query));

    if (!isValidQuery(query, this.props.checkForFields)) {
      newQuery.Filters = [];
    }

    if (typeof this.props?.onSaveAsDefault === "function") {
      this.props.onSaveAsDefault(newQuery);
    }
  }

  /**
   * Clears the filter.
   */
  clear() {
    if (typeof this.props?.onClear === "function") {
      this.props.onClear();

      this.setState({
        advancedFilterQuery: null,
      });
    }
  }

  render() {
    return (
      <>
        <div
          className="pt-2 pl-2 pr-2 mb-3 d-flex justify-content-between align-items-center flex-wrap"
          style={{ rowGap: "10px" }}
        >
          <Nav
            activeKey={this.state.filterMode}
            appearance="subtle"
            style={{ overflowX: "auto" }}
            onSelect={(key) => {
              this.setState({ filterMode: key });
              callFunctionIfExists(this.props.onModeChange, key);
            }}
          >
            <Nav.Item eventKey="quick">
              <div className="d-flex align-items-center pt-0">
                <i
                  style={{ fontSize: 16 }}
                  className="mdi mdi-lightning-bolt pt-0 mt-0 pr-1 text-warning"
                ></i>
                <strong>Quick Filters</strong>
              </div>
            </Nav.Item>
            {this.props.filters && (
              <Nav.Item eventKey="saved" disabled={this.props.disableSave}>
                <div className="d-flex align-items-center pt-0">
                  <i
                    style={{ fontSize: 16 }}
                    className="mdi mdi-content-save pt-0 mt-0 pr-1"
                  />
                  <strong>Saved Filters</strong>
                </div>
              </Nav.Item>
            )}
            <Nav.Item
              eventKey="advanced"
              disabled={this.props.disableAdvancedFilter}
            >
              <div className="d-flex align-items-center pt-0">
                <i
                  style={{ fontSize: 16 }}
                  className="mdi mdi-filter-variant pt-0 mt-0 pr-2"
                />
                <strong>Advanced Filters</strong>
              </div>
            </Nav.Item>
          </Nav>
          <div className="d-flex">
            {this.props.customClearButton ? (
              this.props.customClearButton
            ) : (
              <Button
                size="sm"
                outline
                color="secondary"
                disabled={!this.props.query?.Filters?.length}
                onClick={(e) => {
                  e.stopPropagation();
                  this.props.onClear();
                }}
              >
                {this.props.query?.Filters?.length
                  ? "Clear All"
                  : "No Filters Applied"}
              </Button>
            )}

            {(this.props.saveNewFilter || this.props.saveAsDefault) && (
              <Whisper
                disabled={this.props.disableSave}
                trigger="click"
                placement="auto"
                speaker={
                  <Popover className="sidebar-popover" full>
                    {this.props.saveNewFilter && (
                      <DropdownOptionWithIcon
                        icon="mdi mdi-content-save-plus"
                        name="Save as New Filter"
                        onClick={(e) => {
                          e.stopPropagation();
                          this.props.saveNewFilter();
                        }}
                      />
                    )}
                    <DropdownOptionWithIcon
                      icon="mdi mdi-content-save"
                      name="Save as Default"
                      onClick={(e) => {
                        e.stopPropagation();
                        this.saveAsDefault(this.state.advancedFilterQuery);
                      }}
                    />
                  </Popover>
                }
              >
                <Button
                  size="sm"
                  color="primary"
                  disabled={
                    filterIsSame(this.props.query, this.props.defaultQuery) ||
                    this.props.disableSave
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    //this.saveAsDefault(this.state.advancedFilterQuery);
                  }}
                  className="d-flex align-items-center"
                >
                  <span style={{ fontSize: "0.75rem" }}>Save</span>
                  <i
                    style={{ fontSize: 18 }}
                    className="mdi mdi-chevron-down p-0"
                  />
                </Button>
              </Whisper>
            )}
          </div>
        </div>
        <div>
          {this.state.filterMode === "quick" ? (
            <QuickFilterMenu
              currentModule={this.props.currentModule}
              existingQuery={
                this.state.advancedFilterQuery
                  ? this.state.advancedFilterQuery
                  : null
              }
              onChange={(query) => {
                this.applyAdvancedFilter(query, false, "quick");
              }}
              subitemFieldsOnly={this.props.subitemFieldsOnly || false}
            />
          ) : this.state.filterMode === "advanced" ? (
            <div
              className="p-2"
              style={{
                minHeight: "250px",
                maxHeight: "500px",
                overflowY: "scroll",
                overflowX: "hidden",
              }}
            >
              <AdvancedFilterMenu
                currentModule={this.props.currentModule}
                fields={this.props.fields || null}
                subitemFieldsOnly={this.props.subitemFieldsOnly || false}
                existingQuery={
                  this.state.advancedFilterQuery
                    ? this.state.advancedFilterQuery
                    : null
                }
                onChange={(query) => {
                  this.setState(
                    {
                      advancedFilterQuery: query,
                    },
                    () => {
                      this.props.applyOnChange &&
                        this.applyAdvancedFilter(
                          this.state.advancedFilterQuery,
                          undefined,
                          "advanced"
                        );
                    }
                  );

                  if (
                    !isValidQuery(query, this.props.checkForFields) ||
                    filterIsSame(
                      this.props.query,
                      this.state.advancedFilterQuery
                    )
                  ) {
                    this.setState({
                      allowAdvancedApply: false,
                    });
                  } else {
                    this.setState({ allowAdvancedApply: true });
                  }
                }}
                showVariables={this.props.showVariables}
                showOrderBy={false}
                showLimit={false}
              />
            </div>
          ) : this.state.filterMode === "saved" ? (
            <FiltersOptions
              currentFilter={this.props.query?.Filters}
              savedFilters={this.props.filters}
              openManage={() => {
                this.props.filterDropdownRef?.current?.close();
                this.props.setManageDrawerOpen(true);
              }}
              updateFilters={this.props.handleUpdateFilters()}
            />
          ) : null}
        </div>
        <div
          className="p-2 d-flex justify-content-between"
          style={{ borderTop: "0.5px solid rgba(100,100,100,0.2)" }}
        >
          <div>
            {this.props.defaultQuery && (
              <Button
                outline
                size="sm"
                className="d-flex align-items-center"
                color="primary"
                disabled={filterIsSame(
                  this.props.query,
                  this.props.defaultQuery
                )}
                onClick={() => {
                  this.props.onApply(this.props.defaultQuery, false);
                }}
              >
                <i className="mdi mdi-sync" style={{ fontSize: "14px" }}></i>
                <p
                  className="m-0"
                  style={{ fontSize: "12px", fontWeight: "bold" }}
                >
                  Switch to Default Filter
                </p>
              </Button>
            )}
          </div>

          {this.state.filterMode === "advanced" &&
            !this.props.applyOnChange && (
              <div>
                <Button
                  size="sm"
                  outline
                  color="secondary"
                  onClick={() => {
                    if (typeof this.props.onClose === "function") {
                      this.props.onClose();
                    }
                  }}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  outline
                  color="primary"
                  disabled={!this.state.allowAdvancedApply}
                  onClick={() => {
                    this.applyAdvancedFilter(
                      this.state.advancedFilterQuery,
                      undefined,
                      "advanced"
                    );
                  }}
                >
                  {filterIsSame(
                    this.props.query,
                    this.state.advancedFilterQuery
                  )
                    ? "Filter Applied"
                    : "Apply"}
                </Button>
              </div>
            )}
        </div>
      </>
    );
  }
}

FilterDropdownMenu.defaultProps = {
  showVariables: false,
  checkForFields: true,
  customSaveButton: null,
  customClearButton: null,
  startingMode: null,
};

export default FilterDropdownMenu;
